import { format } from "date-fns";
import { enUS } from "date-fns/locale";

import Calendar from "date-bengali-revised";
// Function to convert English numerals to Bangla numerals
const convertToBanglaNumerals = (numberString) => {
  const banglaNumerals = ["০", "১", "২", "৩", "৪", "৫", "৬", "৭", "৮", "৯"];
  return numberString.replace(/\d/g, (digit) => banglaNumerals[digit]);
};

const bnLocale = {
  ...enUS,
  code: "bn",
  formatDistance: (token, count, options) => {
    const formatDistanceLocale = {
      lessThanXSeconds: "কম 1 সেকেন্ড",
      xSeconds: `${count} সেকেন্ড`,
      halfAMinute: "আধ মিনিট",
      lessThanXMinutes: "কম 1 মিনিট",
      xMinutes: `${count} মিনিট`,
      aboutXHours: "প্রায় 1 ঘন্টা",
      xHours: `${count} ঘন্টা`,
      xDays: `${count} দিন`,
      aboutXMonths: "প্রায় 1 মাস",
      xMonths: `${count} মাস`,
      aboutXYears: "প্রায় 1 বছর",
      xYears: `${count} বছর`,
      overXYears: "1 বছরেরও বেশি",
      almostXYears: "প্রায় 1 বছর",
    };
    return formatDistanceLocale[token];
  },
  formatLong: {
    date: () => "dd-MM-yyyy",
    time: () => "hh:mm aa",
    dateTime: () => "dd-MM-yyyy hh:mm aa",
  },
  localize: {
    ...enUS.localize,
    month: (n, { width }) =>
      [
        "জানুয়ারী",
        "ফেব্রুয়ারী",
        "মার্চ",
        "এপ্রিল",
        "মে",
        "জুন",
        "জুলাই",
        "আগস্ট",
        "সেপ্টেম্বর",
        "অক্টোবর",
        "নভেম্বর",
        "ডিসেম্বর",
      ][n],
    day: (n, { width }) =>
      [
        "রবিবার",
        "সোমবার",
        "মঙ্গলবার",
        "বুধবার",
        "বৃহস্পতিবার",
        "শুক্রবার",
        "শনিবার",
      ][n],
  },
};

const bnLocaleName = {
  ...enUS,
  code: "bn",
  formatDistance: (token, count, options) => {
    const formatDistanceLocale = {
      lessThanXSeconds: "কম ১ সেকেন্ড",
      xSeconds: `${count} সেকেন্ড`,
      halfAMinute: "আধ মিনিট",
      lessThanXMinutes: "কম ১ মিনিট",
      xMinutes: `${count} মিনিট`,
      aboutXHours: "প্রায় ১ ঘন্টা",
      xHours: `${count} ঘন্টা`,
      xDays: `${count} দিন`,
      aboutXMonths: "প্রায় ১ মাস",
      xMonths: `${count} মাস`,
      aboutXYears: "প্রায় ১ বছর",
      xYears: `${count} বছর`,
      overXYears: "১ বছরেরও বেশি",
      almostXYears: "প্রায় ১ বছর",
    };
    return formatDistanceLocale[token];
  },
  formatLong: {
    date: () => "dd-MM-yyyy",
    time: () => "hh:mm aa",
    dateTime: () => "dd-MM-yyyy hh:mm aa",
  },
  localize: {
    ...enUS.localize,
    month: (n, { width }) =>
      [
        "বৈশাখ",
        "জ্যৈষ্ঠ",
        "আষাঢ়",
        "শ্রাবণ",
        "ভাদ্র",
        "আশ্বিন",
        "কার্তিক",
        "অগ্রহায়ণ",
        "পৌষ",
        "মাঘ",
        "ফাল্গুন",
        "চৈত্র",
      ][n],
    day: (n, { width }) =>
      [
        "রবিবার",
        "সোমবার",
        "মঙ্গলবার",
        "বুধবার",
        "বৃহস্পতিবার",
        "শুক্রবার",
        "শনিবার",
      ][n],
  },
};

const formatWithBanglaNumerals = (date, formatString, locale) => {
  if (!date) {
    return date;
  }
  const formattedDate = format(date, formatString, { locale });
  return convertToBanglaNumerals(formattedDate);
};

export const localDateTimeFormat = (dateString) => {
  if (!dateString) {
    return dateString;
  }
  const date = new Date(dateString);
  return formatWithBanglaNumerals(date, "dd-MM-yyyy hh:mm aa", bnLocale);
};
export const localTimeFormat = (dateString) => {
  if (!dateString) {
    return dateString;
  }
  const date = new Date(dateString);
  return formatWithBanglaNumerals(date, "hh:mm aa", bnLocale);
};

export const localTime = (timeString) => {
  if (!timeString) return timeString;

  // Split the `hearingTime` into hours and minutes
  const [hours, minutes] = timeString.split(":").map(Number);

  // Create a Date object with today’s date and given time
  const date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes);

  // Use `Intl.DateTimeFormat` to format in Bangla and `12-hour` time format
  return new Intl.DateTimeFormat("bn-BD", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h12",
  }).format(date);
};

export const localDateFormat = (dateString) => {
  if (!dateString) {
    return dateString;
  }


  // Create a new Date object and set the time to midnight
  const date = new Date(dateString);
  date.setHours(0, 0, 0, 0); // Set time to midnight to remove timezone effect

  // Check if the date is invalid
  if (isNaN(date.getTime())) {
    return "Invalid date"; // Return an appropriate fallback
  }

  if (dateString.length === 4) {
    return formatWithBanglaNumerals(date, "yyyy", bnLocale);
  }
  // Format the date using the Bangla numeral formatter
  return formatWithBanglaNumerals(date, "dd-MM-yyyy", bnLocale);
};

export const localDateFormatName = (dateString) => {
  if (!dateString) {
    return dateString;
  }
  const date = new Date(dateString);
  return formatWithBanglaNumerals(date, "d MMM, yyyy", bnLocale);
};
export const localDateFormatNameBN = (dateString) => {
  if (!dateString) {
    return dateString;
  }
  const todaydate = new Date(dateString);
  let cal = new Calendar();
  cal.fromDate(todaydate);

  // Formatting the date in Bengali
  const formattedDate = cal.format("D MMMM, Y");
  return formattedDate;
};
export const enLocalDateFormatName = (dateString) => {
  if (!dateString) {
    return dateString;
  }
  const date = new Date(dateString);
  return format(date, "d MMM, yyyy", bnLocale);
};

export const enLocalDateTimeFormat = (dateString) => {
  if (!dateString) {
    return dateString;
  }
  const date = new Date(dateString);
  return format(date, "dd-MM-yyyy hh:mm aa");
};

export const enLocalDateFormat = (dateString) => {
  if (!dateString) {
    return dateString;
  }
  const date = new Date(dateString);
  return format(date, "dd-MM-yyyy");
};
export const enlocalDateFormat = (dateString) => {
  if (!dateString) {
    return dateString;
  }
  const date = new Date();
  return format(date, "MMMM d, yyyy");
};

export const globalDateFormat = (dateString) => {
  if (!dateString) {
    return dateString;
  }
  if (!dateString) {
    return dateString;
  }
  const date = new Date(dateString);
  return format(date, "yyyy-MM-dd");
};


// dateUtils.js
export const mergeDateValues = ({ year, month, day }) => {
  if (!year) return "";

  const y = String(year).split("-")[0].substring(0, 4);

  const m = Number(month);
  const d = Number(day);

  if (!m) return y;

  const mm = String(m).padStart(2, "0");

  if (!d) return `${y}-${mm}`;

  const dd = String(d).padStart(2, "0");

  return `${y}-${mm}-${dd}`;
};


